import React, { createContext, useContext, useState } from 'react';
import type { TradingAccount } from '../types';
import { sanitizeAccount } from '../utils/securityUtils';

interface TradingAccountContextType {
  currentAccount: TradingAccount | null;
  setCurrentAccount: (account: TradingAccount | null) => void;
}

const TradingAccountContext = createContext<TradingAccountContextType | undefined>(undefined);

const SELECTED_ACCOUNT_KEY = 'selectedTradingAccount';

export function TradingAccountProvider({ children }: { children: React.ReactNode }) {
  const [currentAccount, setCurrentAccountState] = useState<TradingAccount | null>(() => {
    try {
      const savedAccount = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      return savedAccount ? JSON.parse(savedAccount) : null;
    } catch (error) {
      // console.error supprimé
      return null;
    }
  });

  const setCurrentAccount = (account: TradingAccount | null) => {
    setCurrentAccountState(account);
    if (account) {
      // Sanitize account before storing in localStorage
      const sanitizedAccount = sanitizeAccount(account);
      localStorage.setItem(SELECTED_ACCOUNT_KEY, JSON.stringify(sanitizedAccount));
    } else {
      localStorage.removeItem(SELECTED_ACCOUNT_KEY);
    }
  };

  return (
    <TradingAccountContext.Provider value={{ currentAccount, setCurrentAccount }}>
      {children}
    </TradingAccountContext.Provider>
  );
}

export function useTradingAccount() {
  const context = useContext(TradingAccountContext);
  if (context === undefined) {
    throw new Error('useTradingAccount must be used within a TradingAccountProvider');
  }
  return context;
}

